import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  ReactCompareSlider,
  ReactCompareSliderImage
} from 'react-compare-slider';
import './CaseStudy.css';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Editorial case-study layout: solid black surface, Melodrama display type, curated frames.
 * Deliberately not wrapped in `.project`: those legacy rules force 16px indented body copy.
 *
 * Optional story props:
 * - brief: { they: { title, body }, me: { title, body } }
 * - problem: { title?, lead, detail, image?: { src, alt?, caption? } }
 * - solutions: [{ title, lead, detail, images, video? }]
 * - compares: [{ before, after, caption, beforeAlt?, afterAlt? }]
 * narrative items can be a string, or { title, lead, detail } for skim + depth.
 */
const CaseStudy = ({
  accent = '#ffffff',
  className = '',
  eyebrow = 'Selected work',
  title,
  summary,
  links = [],
  hero,
  date,
  role,
  stack = [],
  metrics = [],
  outcome,
  brief,
  problem,
  solutions = [],
  solutionsLabel = 'What shipped',
  compares = [],
  comparesLabel = 'Before & after',
  narrative = [],
  decisions = [],
  video,
  images = [],
  next
}) => {
  const rootRef = useRef(null);
  const heroFrameRef = useRef(null);
  const progressRef = useRef(null);
  const [entered, setEntered] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const solutionImages = solutions.flatMap((chapter) => chapter.images || []);
  const problemImage = problem?.image?.src ? problem.image : null;
  const frames = [
    ...(problemImage ? [problemImage] : []),
    ...solutionImages,
    ...images
  ];

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const targets = root.querySelectorAll('[data-reveal]');
    if (prefersReducedMotion()) {
      targets.forEach((node) => node.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );

    targets.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [
    images.length,
    decisions.length,
    narrative.length,
    metrics.length,
    solutions.length,
    compares.length,
    brief,
    problem
  ]);

  useEffect(() => {
    const frame = heroFrameRef.current;
    if (!frame || prefersReducedMotion()) return undefined;

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = frame.getBoundingClientRect();
        const viewH = window.innerHeight || 1;
        const progress = (viewH - rect.top) / (viewH + rect.height);
        const clamped = Math.min(1, Math.max(0, progress));
        // Keep parallax subtle so mockup edges stay in frame.
        frame.style.setProperty('--cs-parallax', `${(clamped - 0.5) * 18}px`);
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
    };
  }, [hero?.src]);

  useEffect(() => {
    const bar = progressRef.current;
    const root = rootRef.current;
    if (!bar || !root) return undefined;

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const travel = root.scrollHeight - window.innerHeight;
        const read = travel > 0 ? window.scrollY / travel : 0;
        bar.style.transform = `scaleX(${Math.min(1, Math.max(0, read))})`;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event) => {
      if (event.key === 'Escape') setLightboxIndex(null);
      if (event.key === 'ArrowRight') {
        setLightboxIndex((i) => (i === null ? i : (i + 1) % frames.length));
      }
      if (event.key === 'ArrowLeft') {
        setLightboxIndex((i) =>
          i === null ? i : (i - 1 + frames.length) % frames.length
        );
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [lightboxIndex, frames.length]);

  /**
   * Pick contain vs cover from the image vs the tile.
   * UI screenshots (wide) in portrait slots used to get harshly cropped.
   */
  const fitToTile = (event) => {
    const img = event.currentTarget;
    const media = img.parentElement;
    if (!media || !img.naturalWidth || !img.naturalHeight) return;

    media.classList.remove('is-portrait', 'is-landscape', 'is-contain');

    const imageRatio = img.naturalWidth / img.naturalHeight;
    if (imageRatio < 0.92) media.classList.add('is-portrait');
    else media.classList.add('is-landscape');

    const applyFit = () => {
      const { width, height } = media.getBoundingClientRect();
      if (!width || !height) return;
      const tileRatio = width / height;
      const mismatch =
        Math.abs(imageRatio - tileRatio) / Math.max(imageRatio, tileRatio);
      // Show the full frame when shapes disagree, or whenever the asset is portrait
      if (mismatch > 0.16 || imageRatio < 0.92) {
        media.classList.add('is-contain');
      }
    };

    requestAnimationFrame(applyFit);
  };

  const frameIndex = (shot) =>
    frames.findIndex((frame) => frame.src === shot.src);

  const titleWords = String(title || '').split(' ');
  const lead = images[0];
  const duo = images.slice(1, 3);
  const strip = images.slice(3);
  const outcomeLines = String(outcome || '')
    .split(/\n|(?<=\.)\s+(?=[A-Z0-9“"])/)
    .map((line) => line.trim())
    .filter(Boolean);

  const openFrame = (shot) => {
    const index = frameIndex(shot);
    if (index >= 0) setLightboxIndex(index);
  };

  const renderBriefBody = (body) => {
    if (Array.isArray(body)) {
      return body.map((paragraph, index) => (
        <p className="caseStudy__briefBody" key={index}>
          {paragraph}
        </p>
      ));
    }

    return <p className="caseStudy__briefBody">{body}</p>;
  };

  const renderStoryDetail = (detail) => {
    if (Array.isArray(detail)) {
      return detail.map((paragraph, index) => (
        <p className="caseStudy__storyDetail" key={index}>
          {paragraph}
        </p>
      ));
    }

    return detail ? <p className="caseStudy__storyDetail">{detail}</p> : null;
  };

  const renderStoryBlock = (block, index) => {
    if (typeof block === 'string' || typeof block === 'number') {
      return <p key={index}>{block}</p>;
    }

    if (block?.lead || block?.title) {
      return (
        <article className="caseStudy__story" key={block.title || index}>
          {block.title ? (
            <h3 className="caseStudy__storyTitle">{block.title}</h3>
          ) : null}
          {block.lead ? (
            <p className="caseStudy__storyLead">{block.lead}</p>
          ) : null}
          {renderStoryDetail(block.detail)}
        </article>
      );
    }

    return <p key={index}>{block}</p>;
  };

  return (
    <article
      ref={rootRef}
      className={`caseStudy ${className} ${entered ? 'is-entered' : ''}`.trim()}
      style={{ '--cs-accent': accent }}
    >
      <span className="caseStudy__progress" aria-hidden="true">
        <span className="caseStudy__progressBar" ref={progressRef} />
      </span>

      <div className="caseStudy__shell">
        <header className="caseStudy__hero">
          <div className="caseStudy__heroTop">
            <div className="caseStudy__heroIntro">
              <p className="caseStudy__eyebrow">{eyebrow}</p>
              <h1 className="caseStudy__title">
                {titleWords.map((word, index) => (
                  <span className="caseStudy__titleLine" key={`${word}-${index}`}>
                    <span
                      className="caseStudy__titleWord"
                      style={{ transitionDelay: `${0.14 + index * 0.08}s` }}
                    >
                      {word}
                      {index === titleWords.length - 1 ? (
                        <em className="caseStudy__titleAccent">.</em>
                      ) : null}
                    </span>
                  </span>
                ))}
              </h1>
              {summary ? <p className="caseStudy__summary">{summary}</p> : null}
              {links.length > 0 ? (
                <div className="caseStudy__ctaRow">
                  {links.map((link, index) => (
                    <a
                      key={`${link.href}-${index}`}
                      className={
                        index === 0
                          ? 'caseStudy__cta'
                          : 'caseStudy__cta caseStudy__cta--ghost'
                      }
                      href={link.href}
                      target={link.href.startsWith('#') ? undefined : '_blank'}
                      rel={link.href.startsWith('#') ? undefined : 'noreferrer'}
                    >
                      <span>{link.label}</span>
                      <i className="caseStudy__ctaArrow" aria-hidden="true">
                        →
                      </i>
                    </a>
                  ))}
                </div>
              ) : null}
            </div>

            <aside className="caseStudy__heroAside" aria-label="Project facts">
              {date ? (
                <div className="caseStudy__heroAsideItem">
                  <span>Timeline</span>
                  <p>{date}</p>
                </div>
              ) : null}
              {role ? (
                <div className="caseStudy__heroAsideItem">
                  <span>Contribution</span>
                  <p>{role}</p>
                </div>
              ) : null}
              {stack.length > 0 ? (
                <div className="caseStudy__heroAsideItem">
                  <span>Stack</span>
                  <ul className="caseStudy__stack">
                    {stack.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </aside>
          </div>

          {hero?.src ? (
            <figure className="caseStudy__heroStage">
              <div className="caseStudy__heroFrame" ref={heroFrameRef}>
                <img src={hero.src} alt={hero.alt || `${title} hero`} />
                <span className="caseStudy__heroCurtain" aria-hidden="true" />
                <span className="caseStudy__heroScrim" aria-hidden="true" />
              </div>
              {hero.caption ? (
                <figcaption className="caseStudy__heroCaption">
                  <span>{hero.caption}</span>
                  <span>Fig. 01</span>
                </figcaption>
              ) : null}
            </figure>
          ) : null}
        </header>

        {brief?.they || brief?.me ? (
          <section className="caseStudy__brief" data-reveal>
            <p className="caseStudy__sectionLabel">
              {brief.label || 'The relationship'}
            </p>
            <div className="caseStudy__briefGrid">
              {brief.they ? (
                <article className="caseStudy__briefCard">
                  <h2 className="caseStudy__briefTitle">
                    {brief.they.title || 'Who they are'}
                  </h2>
                  {renderBriefBody(brief.they.body)}
                </article>
              ) : null}
              {brief.me ? (
                <article className="caseStudy__briefCard">
                  <h2 className="caseStudy__briefTitle">
                    {brief.me.title || 'Who I was'}
                  </h2>
                  {renderBriefBody(brief.me.body)}
                </article>
              ) : null}
            </div>
          </section>
        ) : null}

        {problem ? (
          <section
            className={`caseStudy__problem${
              problemImage ? ' caseStudy__problem--withMedia' : ''
            }`}
            data-reveal
          >
            <div className="caseStudy__problemCopy">
              <p className="caseStudy__sectionLabel">The problem</p>
              {problem.title ? (
                <h2 className="caseStudy__problemTitle">{problem.title}</h2>
              ) : null}
              {problem.lead ? (
                <p className="caseStudy__problemLead">{problem.lead}</p>
              ) : null}
              {problem.detail ? (
                <p className="caseStudy__problemDetail">{problem.detail}</p>
              ) : null}
            </div>
            {problemImage ? (
              <figure
                className="caseStudy__problemFigure"
                onClick={() => openFrame(problemImage)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openFrame(problemImage);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="caseStudy__shotMedia">
                  <img
                    src={problemImage.src}
                    alt={
                      problemImage.alt ||
                      problemImage.caption ||
                      `${title} problem`
                    }
                    onLoad={fitToTile}
                  />
                  <span className="caseStudy__frameGlow" aria-hidden="true" />
                </div>
                <figcaption>
                  <span>
                    {problemImage.caption || 'Before'}
                  </span>
                  <em>
                    {String(frameIndex(problemImage) + 1).padStart(2, '0')}
                  </em>
                </figcaption>
              </figure>
            ) : null}
          </section>
        ) : null}

        {(metrics.length > 0 || outcome) && (
          <section className="caseStudy__impact" data-reveal>
            {metrics.length > 0 ? (
              <ul className="caseStudy__metrics">
                {metrics.map((metric, index) => (
                  <li
                    className="caseStudy__metric"
                    key={`${metric.value}-${metric.label}`}
                    style={{ transitionDelay: `${0.08 + index * 0.07}s` }}
                  >
                    <span className="caseStudy__metricValue">{metric.value}</span>
                    <span className="caseStudy__metricLabel">{metric.label}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {outcome ? (
              <p className="caseStudy__outcomeText">
                {outcomeLines.map((line, index) => (
                  <span
                    className="caseStudy__outcomeLine"
                    key={`${line}-${index}`}
                    style={{ transitionDelay: `${0.12 + index * 0.1}s` }}
                  >
                    {line}
                  </span>
                ))}
              </p>
            ) : null}
          </section>
        )}

        {solutions.length > 0 ? (
          <section
            id={compares.length === 0 ? 'frames' : undefined}
            className="caseStudy__solutions"
          >
            <div className="caseStudy__solutionsHead" data-reveal>
              <p className="caseStudy__sectionLabel">{solutionsLabel}</p>
              <span className="caseStudy__solutionsRule" aria-hidden="true" />
            </div>
            {solutions.map((chapter, chapterIndex) => (
              <article
                className={`caseStudy__chapter${chapterIndex === 0 ? ' caseStudy__chapter--first' : ''}`}
                key={chapter.title || chapterIndex}
                data-reveal
              >
                <div className="caseStudy__chapterCopy">
                  <p className="caseStudy__chapterIndex">
                    {String(chapterIndex + 1).padStart(2, '0')}
                  </p>
                  {chapter.title ? (
                    <h3 className="caseStudy__chapterTitle">{chapter.title}</h3>
                  ) : null}
                  {chapter.lead ? (
                    <p className="caseStudy__chapterLead">{chapter.lead}</p>
                  ) : null}
                  {chapter.detail ? (
                    <p className="caseStudy__chapterDetail">{chapter.detail}</p>
                  ) : null}
                </div>
                {chapter.video ? (
                  <div className="caseStudy__chapterVideo">
                    <div className="caseStudy__video">
                      <iframe
                        src={`${chapter.video}${chapter.video.includes('?') ? '&' : '?'}rel=0`}
                        title={`${chapter.title || title} walkthrough`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    {chapter.videoCaption ? (
                      <p className="caseStudy__chapterVideoCaption">
                        {chapter.videoCaption}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {chapter.images?.length ? (
                  <div
                    className={`caseStudy__chapterFrames caseStudy__chapterFrames--${Math.min(chapter.images.length, 4)}`}
                  >
                    {chapter.images.map((shot) => {
                      const index = frameIndex(shot);
                      return (
                        <figure
                          className="caseStudy__chapterShot"
                          key={shot.src}
                          onClick={() => openFrame(shot)}
                        >
                          <span className="caseStudy__shotMedia">
                            <img
                              src={shot.src}
                              alt={shot.alt}
                              onLoad={fitToTile}
                            />
                            <span
                              className="caseStudy__frameGlow"
                              aria-hidden="true"
                            />
                          </span>
                          <figcaption>
                            <em>{String(index + 1).padStart(2, '0')}</em>
                            <span>{shot.caption || shot.alt}</span>
                          </figcaption>
                        </figure>
                      );
                    })}
                  </div>
                ) : null}
              </article>
            ))}
          </section>
        ) : null}

        {compares.length > 0 ? (
          <section id="frames" className="caseStudy__compares">
            <div className="caseStudy__solutionsHead" data-reveal>
              <p className="caseStudy__sectionLabel">{comparesLabel}</p>
              <span className="caseStudy__solutionsRule" aria-hidden="true" />
            </div>
            <p className="caseStudy__comparesHint" data-reveal>
              Drag the handle to compare old WordPress pages with the redesign.
            </p>
            <div className="caseStudy__compareGrid">
              {compares.map((pair, index) => (
                <figure
                  className="caseStudy__compare"
                  key={`${pair.caption || 'compare'}-${index}`}
                  data-reveal
                  style={{ transitionDelay: `${index * 0.06}s` }}
                >
                  <ReactCompareSlider
                    className="caseStudy__compareSlider"
                    itemOne={
                      <ReactCompareSliderImage
                        src={pair.before}
                        alt={pair.beforeAlt || `Before: ${pair.caption || title}`}
                      />
                    }
                    itemTwo={
                      <ReactCompareSliderImage
                        src={pair.after}
                        alt={pair.afterAlt || `After: ${pair.caption || title}`}
                      />
                    }
                  />
                  {pair.caption ? (
                    <figcaption>
                      <em>{String(index + 1).padStart(2, '0')}</em>
                      <span>{pair.caption}</span>
                    </figcaption>
                  ) : null}
                </figure>
              ))}
            </div>
          </section>
        ) : null}

        {(narrative.length > 0 || decisions.length > 0) && (
          <section
            className={`caseStudy__body${narrative.length === 0 ? ' caseStudy__body--solo' : ''}`}
          >
            {narrative.length > 0 ? (
              <div className="caseStudy__narrative" data-reveal>
                <p className="caseStudy__sectionLabel">Context</p>
                {narrative.map(renderStoryBlock)}
              </div>
            ) : null}

            {decisions.length > 0 ? (
              <div className="caseStudy__decisions">
                <p className="caseStudy__sectionLabel" data-reveal>
                  Decisions
                </p>
                <ol className="caseStudy__decisionList">
                  {decisions.map((decision, index) => (
                    <li
                      className="caseStudy__decision"
                      key={decision.title}
                      data-reveal
                      style={{ transitionDelay: `${index * 0.08}s` }}
                    >
                      <span className="caseStudy__decisionIndex">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="caseStudy__decisionCopy">
                        <h3 className="caseStudy__decisionTitle">{decision.title}</h3>
                        <p className="caseStudy__decisionBody">{decision.body}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </section>
        )}

        {video && !solutions.some((chapter) => chapter.video) ? (
          <section className="caseStudy__section" data-reveal>
            <p className="caseStudy__sectionLabel">Walkthrough</p>
            <div className="caseStudy__video">
              <iframe
                src={`${video}${video.includes('?') ? '&' : '?'}rel=0`}
                title={`${title} walkthrough`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </section>
        ) : null}

        {images.length > 0 ? (
          <section id="frames" className="caseStudy__gallery">
            <div className="caseStudy__galleryHead" data-reveal>
              <div className="caseStudy__galleryIntro">
                <p className="caseStudy__sectionLabel">Selected frames</p>
                <h2 className="caseStudy__galleryTitle">
                  A few surfaces from the build.
                </h2>
              </div>
              <p className="caseStudy__galleryHint">Click to expand</p>
            </div>

            {lead ? (
              <figure
                className="caseStudy__feature"
                data-reveal
                onClick={() => openFrame(lead)}
              >
                <span className="caseStudy__featureMedia">
                  <img src={lead.src} alt={lead.alt} />
                  <span className="caseStudy__frameGlow" aria-hidden="true" />
                </span>
                <figcaption className="caseStudy__featureCaption">
                  <em>{String(frameIndex(lead) + 1).padStart(2, '0')}</em>
                  <span>{lead.caption || lead.alt}</span>
                </figcaption>
              </figure>
            ) : null}

            {duo.length > 0 ? (
              <div className="caseStudy__duo">
                {duo.map((shot, index) => (
                  <figure
                    className={`caseStudy__duoShot caseStudy__duoShot--${index === 0 ? 'tall' : 'offset'}`}
                    key={shot.src}
                    data-reveal
                    style={{ transitionDelay: `${index * 0.1}s` }}
                    onClick={() => openFrame(shot)}
                  >
                    <span className="caseStudy__shotMedia">
                      <img src={shot.src} alt={shot.alt} onLoad={fitToTile} />
                      <span className="caseStudy__frameGlow" aria-hidden="true" />
                    </span>
                    <figcaption>
                      <em>{String(frameIndex(shot) + 1).padStart(2, '0')}</em>
                      <span>{shot.caption || shot.alt}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            ) : null}

            {strip.length > 0 ? (
              <div
                className={`caseStudy__mosaic caseStudy__mosaic--${Math.min(strip.length, 4)}`}
                data-reveal
              >
                {strip.map((shot, index) => (
                  <figure
                    className="caseStudy__mosaicShot"
                    key={shot.src}
                    style={{ transitionDelay: `${index * 0.07}s` }}
                    onClick={() => openFrame(shot)}
                  >
                    <span className="caseStudy__shotMedia">
                      <img src={shot.src} alt={shot.alt} onLoad={fitToTile} />
                      <span className="caseStudy__frameGlow" aria-hidden="true" />
                    </span>
                    <figcaption>
                      <em>{String(frameIndex(shot) + 1).padStart(2, '0')}</em>
                      <span>{shot.caption || shot.alt}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        {next?.link ? (
          <footer className="caseStudy__next" data-reveal>
            <Link className="caseStudy__nextInner" to={next.link}>
              <span className="caseStudy__nextCopy">
                <span className="caseStudy__nextLabel">Next project</span>
                <span className="caseStudy__nextTitle">
                  {next.label || 'Continue'}
                </span>
              </span>
              <span className="caseStudy__nextArrow" aria-hidden="true">
                →
              </span>
            </Link>
          </footer>
        ) : null}
      </div>

      {lightboxIndex !== null && frames[lightboxIndex]
        ? createPortal(
            <div
              className="caseStudy__lightbox"
              role="dialog"
              aria-modal="true"
              onClick={() => setLightboxIndex(null)}
            >
              <button
                type="button"
                className="caseStudy__lightboxClose"
                aria-label="Close"
                onClick={() => setLightboxIndex(null)}
              >
                ×
              </button>
              {frames.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="caseStudy__lightboxNav caseStudy__lightboxNav--prev"
                    aria-label="Previous image"
                    onClick={(event) => {
                      event.stopPropagation();
                      setLightboxIndex(
                        (lightboxIndex - 1 + frames.length) % frames.length
                      );
                    }}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="caseStudy__lightboxNav caseStudy__lightboxNav--next"
                    aria-label="Next image"
                    onClick={(event) => {
                      event.stopPropagation();
                      setLightboxIndex((lightboxIndex + 1) % frames.length);
                    }}
                  >
                    ›
                  </button>
                </>
              ) : null}
              <img
                src={frames[lightboxIndex].src}
                alt={frames[lightboxIndex].alt}
                onClick={(event) => event.stopPropagation()}
              />
            </div>,
            document.body
          )
        : null}
    </article>
  );
};

export default CaseStudy;
